import { AvSubsystemAvServiceType, DeviceErrors } from "@minervaproject/avsubsystem-core";
import { useCallback, useContext, useMemo } from "react";

import {
  BarePubsubVirtualClassStateMessageFromClientType,
  PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
  PubsubVirtualUserState,
} from "../shared/types/av-state";

import { filterMap } from "./collections";
import { isTestEnv } from "./env";
import { isAVEnabled, isAvMobileDevicesEnabled } from "./waffle-flags";

import { serverLowerHand, serverRaiseHand } from "api/api-server-actions";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectCurrentClassUser,
  selectCurrentClassUserId,
  selectIsClassSessionVirtual,
  selectUserAgentInfo,
} from "store/selectors";
import {
  selectClassStateLastUpdatedAt,
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { useObjectSelector } from "store/store-hooks";

export const AV_SUPPORTED_BROWSERS = ["Chrome"];

export const useAv = () => {
  const currentClassUser = useObjectSelector(selectCurrentClassUser, [
    "id",
    "is_in_class_session",
    "is_performing_av_check",
    "hand_raised_at",
  ]);
  const isVirtualClassActive = useAppSelector(selectIsClassSessionVirtual) && isAVEnabled();
  const isMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const isNotMobile = useIsMdOrLarger() || isAvMobileDevicesEnabled();
  const isAvSupportedInBrowser =
    useAppSelector(selectUserAgentInfo)?.isBrowserAvCompatible && isNotMobile;
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);

  const hasPubsubVirtualClassState = !!useAppSelector(selectClassStateLastUpdatedAt);

  const isAvActiveForVirtualUser =
    isVirtualClassActive &&
    (currentClassUser?.is_in_class_session || currentClassUser?.is_performing_av_check);
  const isAvActiveInAnotherTab =
    isAvActiveForVirtualUser && hasPubsubVirtualClassState && !isMainAvTab;

  return useMemo(() => {
    const isInVirtualClass = (isMainAvTab && isAvActiveForVirtualUser) ?? false;
    const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;
    const isHandRaised = !!currentClassUser?.hand_raised_at;
    const hasClassUser = !!currentClassUser?.id;

    const toggleHand = async () => {
      if (!currentClassUser?.id) {
        console.error("[toggleHand] No current virtual user ID");
        return;
      }
      return isHandRaised
        ? serverLowerHand(currentClassUser.id)
        : serverRaiseHand(currentClassUser.id);
    };

    return {
      hasClassUser,
      isAvActiveInAnotherTab,
      isInVirtualClass,
      isInVirtualClassOrObserving,
      isVirtualClassActive,
      isAvSupportedInBrowser,
      isHandRaised,
      toggleHand,
    };
  }, [
    isMainAvTab,
    isAvActiveForVirtualUser,
    currentClassUser?.hand_raised_at,
    currentClassUser?.id,
    isAvActiveInAnotherTab,
    isVirtualClassActive,
    isAvSupportedInBrowser,
    isObservingClass,
  ]);
};

export const useAvPubsubMessaging = () => {
  const { sendPubsubMessage, pubsubStatus } = useContext(PubsubWebsocketContext);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const classSessionId = useAppSelector(selectActiveClassSessionId);

  const sendPubsubVirtualClassStateMessage = useCallback(
    (message: BarePubsubVirtualClassStateMessageFromClientType) => {
      if (!currentClassUserId) {
        if (!isTestEnv) {
          console.warn("[AvPubsubMessaging] Cannot send message: missing ClassUser");
        }
        return;
      }
      if (pubsubStatus !== "connected") {
        if (!isTestEnv) {
          console.warn(
            `[AvPubsubMessaging] Cannot send message: pubsub status is '${pubsubStatus}'`,
          );
        }
        return;
      }
      sendPubsubMessage({
        ...message,
        type: PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
        virtual_user_id: currentClassUserId,
        class_session_id: classSessionId,
      });
    },
    [classSessionId, currentClassUserId, pubsubStatus, sendPubsubMessage],
  );

  const sendClassStateUpdate = useCallback(
    (data: PubsubVirtualUserState) => {
      sendPubsubVirtualClassStateMessage({
        action: "update",
        data,
      });
    },
    [sendPubsubVirtualClassStateMessage],
  );

  const sendObservingJoinMessage = useCallback(() => {
    if (pubsubStatus !== "connected") {
      return;
    }
    sendPubsubMessage({
      type: PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
      action: "join",
      force: false,
      // The monitoring virtual_user_id receives all the updates but is not a participant
      virtual_user_id: `monitoring`,
    });
  }, [sendPubsubMessage, pubsubStatus]);

  return { sendPubsubVirtualClassStateMessage, sendClassStateUpdate, sendObservingJoinMessage };
};

export function deviceErrorForDisplay(error: DeviceErrors | null) {
  const errorToDisplay: { [key: string]: string } = {
    // [DeviceErrors.AUDIO_MAC_NOT_ALLOWED]: "audio_mac_not_allowed",
    [DeviceErrors.AUDIO_WINDOWS_CAMERA_IN_USE]: "Mic In Use by Another App (Windows)",
    [DeviceErrors.AUDIO_NOT_ALLOWED]: "Mic Blocked",
    [DeviceErrors.AUDIO_NOT_FOUND]: "No Mic Detected",
    // [DeviceErrors.AUDIO_FAIL]: "audio_fail",
    // [DeviceErrors.SCREENSHARE_FAIL]: "screenshare_fail",
    // [DeviceErrors.VIDEO_MAC_NOT_ALLOWED]: "video_mac_not_allowed",
    [DeviceErrors.VIDEO_WINDOWS_CAMERA_IN_USE]: "Video In Use by Another App (Windows)",
    [DeviceErrors.VIDEO_NOT_ALLOWED]: "Camera Blocked",
    [DeviceErrors.VIDEO_NOT_FOUND]: "No Camera Detected",
    // [DeviceErrors.VIDEO_FAIL]: "video_fail",
    // [DeviceErrors.MEDIA_TIMEOUT]: "media_timeout",
  };

  if (error === null) {
    return "Unknown error";
  }

  return errorToDisplay[error] || error;
}

export function getAvServiceFormItems(): { id: string; title: string }[] {
  return filterMap(
    Object.values(AvSubsystemAvServiceType),
    (value) => typeof value === "string" && value !== AvSubsystemAvServiceType.Fake,
    (value) => ({
      id: value,
      title: value,
    }),
  );
}
